/**
 * `EventService` — `/api/v1/events/*`: CRUD мероприятий и управление составом.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ПРАВА, И ПОЧЕМУ ОНИ ВЫГЛЯДЯТ КАК 404
 * ───────────────────────────────────────────────────────────────────────────
 *
 *   admin        всё без ограничений;
 *   owner        полный доступ к своим мероприятиям, включая состав;
 *   viewer       только чтение своих мероприятий;
 *   не участник  мероприятия для него не существует.
 *
 * ⚠️ «Не существует» буквально: чужое мероприятие отдаёт **404
 * EVENT_NOT_FOUND**, а не 403. Это не недосмотр бэкенда, а его решение — 403
 * подтверждал бы, что такой id есть, и превращал бы перебор id в способ узнать,
 * сколько мероприятий в системе и когда их заводят. 403 `FORBIDDEN` отдаётся
 * только там, где участник УЖЕ знает о существовании мероприятия и ему не
 * хватает роли: viewer, пытающийся править.
 *
 * Следствие для фронта: увидев `EVENT_NOT_FOUND`, нельзя писать «доступ
 * запрещён» — надо назвать обе возможности (см. `lib/api-error.ts`).
 *
 * Авторитет по правам — сервер, а не комментарии здесь: любой скрытый фронтом
 * запрос всё равно получит отказ, если его отправить руками.
 */

import { http } from '@/lib/http'
import type {
  AddEventMemberRequest,
  AddEventMemberResponse,
  CopyEventRequest,
  CopyEventResponse,
  CreateEventMemberUserRequest,
  CreateEventMemberUserResponse,
  CreateEventRequest,
  CreateEventResponse,
  IssueDeviceLoginTicketResponse,
  DeleteEventResponse,
  GetEventResponse,
  ListEventMembersResponse,
  ListEventsRequest,
  ListEventsResponse,
  RemoveEventMemberResponse,
  SetEventMemberRoleRequest,
  SetEventMemberRoleResponse,
  UpdateEventRequest,
  UpdateEventResponse,
} from '@/types/api'

/**
 * UUID в пути кодируется, хотя UUIDv4 спецсимволов и не содержит: дёшево и
 * снимает вопрос «а что если id приехал из `route.params` и оказался мусором».
 */
function path(eventId: string, suffix = ''): string {
  return `/api/v1/events/${encodeURIComponent(eventId)}${suffix}`
}

export const eventsApi = {
  /**
   * `GET /api/v1/events`. Фильтры едут **query-строкой**.
   *
   * admin видит все мероприятия, остальные — только те, в которых состоят.
   * Фильтрация происходит **в запросе**, а не отсеиванием после выборки,
   * поэтому `pageInfo.totalItems` уже посчитан по доступным мероприятиям и
   * страницы не оказываются полупустыми.
   *
   * `signal` — поиск по мере набора обязан гасить предыдущий запрос, иначе
   * ответы приходят вразнобой и в списке оседает результат не последнего
   * нажатия клавиши.
   */
  async list(params: ListEventsRequest = {}, signal?: AbortSignal): Promise<ListEventsResponse> {
    const { data } = await http.get<ListEventsResponse>('/api/v1/events', { params, signal })
    return data
  },

  /** `GET /api/v1/events/{id}`. Чужое мероприятие — 404, а не 403 (см. шапку). */
  async get(eventId: string, signal?: AbortSignal): Promise<GetEventResponse> {
    const { data } = await http.get<GetEventResponse>(path(eventId), { signal })
    return data
  },

  /**
   * `POST /api/v1/events` — **только admin**.
   *
   * ⚠️ Мероприятие создаётся БЕЗ владельцев: их назначают следующим шагом через
   * `addMember`. Между этими двумя действиями мероприятие не видно никому,
   * кроме админов, — ожидаемое состояние, а не переходный дефект. Именно
   * поэтому форма создания уводит человека на вкладку состава, а не в список.
   */
  async create(body: CreateEventRequest): Promise<CreateEventResponse> {
    const { data } = await http.post<CreateEventResponse>('/api/v1/events', body)
    return data
  },

  /**
   * `PATCH /api/v1/events/{id}` — admin или owner, частичное обновление.
   *
   * ⚠️ Проверка «конец не раньше начала» на сервере выполняется **по результату
   * применения патча**, а не по присланным полям: сдвинуть только одну из двух
   * дат — самый обычный способ получить мероприятие, которое кончается до
   * начала. Форма поэтому валидирует пару дат целиком, даже если тронута одна.
   */
  async update(eventId: string, body: UpdateEventRequest): Promise<UpdateEventResponse> {
    const { data } = await http.patch<UpdateEventResponse>(path(eventId), body)
    return data
  },

  /**
   * `DELETE /api/v1/events/{id}` — admin или owner. Удаление **жёсткое**, и
   * каскад уносит справочники, дополнительные поля и всех участников.
   *
   * Мягкого удаления нет намеренно: для «мероприятие не состоится» есть статус
   * `EVENT_STATUS_CANCELLED`. Подтверждение обязано называть последствия.
   *
   * Имя `remove`, а не `delete`: `delete` — зарезервированное слово, и хотя
   * как имя свойства оно легально, о него спотыкается половина линтеров.
   */
  async remove(eventId: string): Promise<DeleteEventResponse> {
    const { data } = await http.delete<DeleteEventResponse>(path(eventId))
    return data
  },

  /**
   * `POST /api/v1/events/{id}/copy` — **только admin**. Копия целиком, КРОМЕ
   * участников.
   *
   * Переносятся состав, зоны, роли участников с их зонами, поля конструктора и
   * макет бейджа вместе с загруженными картинками. НЕ переносятся участники и
   * всё, что на них висит: значения полей, файлы, фотографии.
   *
   * ⚠️ Это должно быть сказано в интерфейсе ДО нажатия, а не в тосте после:
   * «скопировать мероприятие» читается как «скопировать всё», и человек,
   * рассчитывавший получить готовый стартовый список, узнает об обратном на
   * пустом экране участников.
   *
   * Ответ несёт `summary` — счётчики перенесённого. Показывать их стоит
   * буквально: иначе убедиться, что перенеслось всё, можно только обойдя пять
   * экранов копии.
   *
   * У копии свои id у всего, поэтому после успеха идти надо на экран КОПИИ, а
   * не обновлять текущий: ни один id исходника к ней не относится.
   */
  async copy(eventId: string, body: CopyEventRequest): Promise<CopyEventResponse> {
    const { data } = await http.post<CopyEventResponse>(path(eventId, '/copy'), body)
    return data
  },

  /* ───────────────────────────── СОСТАВ ───────────────────────────── */

  /**
   * `GET /api/v1/events/{eventId}/members` — виден **любому** участнику,
   * включая viewer'а: знать, к кому идти за доступом, — это чтение, а не
   * полномочие. Пагинации нет: состав — единицы человек.
   */
  async listMembers(eventId: string, signal?: AbortSignal): Promise<ListEventMembersResponse> {
    const { data } = await http.get<ListEventMembersResponse>(path(eventId, '/members'), { signal })
    return data
  },

  /**
   * `POST /api/v1/events/{eventId}/members` — admin или owner.
   *
   * ⚠️ Повторное добавление — **409**, а не молчаливая смена роли: «добавить» и
   * «переназначить» разные намерения, и склеивать их значит превращать опечатку
   * в понижение чужих прав. Для смены есть `setMemberRole`.
   */
  async addMember(eventId: string, body: AddEventMemberRequest): Promise<AddEventMemberResponse> {
    const { data } = await http.post<AddEventMemberResponse>(path(eventId, '/members'), body)
    return data
  },

  /**
   * `POST /api/v1/events/{eventId}/members/create-user` — завести НОВУЮ учётную
   * запись и сразу включить её в состав. admin или owner.
   *
   * Закрывает дыру в возможностях владельца: `addMember` требует `userId`, а
   * узнать его владелец не может — весь `UserService` admin-only, включая
   * чтение. Без этого метода состав он мог менять только по идентификаторам,
   * которые ему продиктует администратор.
   *
   * ⚠️ Системной роли в теле нет: заводимая запись всегда обычный пользователь.
   * `role` — роль **в мероприятии**. Подробности и последствия (пароль знает
   * заводящий) — в `types/api.ts` над `CreateEventMemberUserRequest`.
   *
   * `409 EMAIL_ALREADY_EXISTS` означает, что человек в системе уже есть; для
   * него нужен `addMember`, а не этот метод.
   */
  async createMemberUser(
    eventId: string,
    body: CreateEventMemberUserRequest,
  ): Promise<CreateEventMemberUserResponse> {
    const { data } = await http.post<CreateEventMemberUserResponse>(
      path(eventId, '/members/create-user'),
      body,
    )
    return data
  },

  /**
   * `POST /api/v1/events/{eventId}/members/{userId}/device-ticket` — выпустить
   * одноразовый код входа для устройства оператора. admin или owner.
   *
   * ⚠️ ОТВЕТ СОДЕРЖИТ CREDENTIAL. Открытый код приходит **один раз** и на
   * сервере не хранится (там только sha256). Он не должен попасть ни в стор,
   * ни в `localStorage`, ни в адресную строку этой вкладки — только в QR на
   * экране и оттуда сразу в телефон оператора.
   *
   * Сервер откажет `VALIDATION_FAILED`, если цель — администратор системы либо
   * отключённая учётная запись; `EVENT_MEMBER_NOT_FOUND`, если человека нет в
   * составе.
   */
  async issueDeviceTicket(
    eventId: string,
    userId: string,
  ): Promise<IssueDeviceLoginTicketResponse> {
    const { data } = await http.post<IssueDeviceLoginTicketResponse>(
      path(eventId, `/members/${encodeURIComponent(userId)}/device-ticket`),
      {},
    )
    return data
  },

  /**
   * `PUT /api/v1/events/{eventId}/members/{userId}` — admin или owner.
   *
   * ⚠️ `409 CANNOT_MODIFY_SELF`: owner не может понизить сам себя — он
   * немедленно потеряет доступ, которым только что распоряжался. admin такого
   * ограничения не имеет, он в мероприятии не состоит.
   */
  async setMemberRole(
    eventId: string,
    userId: string,
    body: SetEventMemberRoleRequest,
  ): Promise<SetEventMemberRoleResponse> {
    const { data } = await http.put<SetEventMemberRoleResponse>(
      path(eventId, `/members/${encodeURIComponent(userId)}`),
      body,
    )
    return data
  },

  /** `DELETE /api/v1/events/{eventId}/members/{userId}`. @see setMemberRole */
  async removeMember(eventId: string, userId: string): Promise<RemoveEventMemberResponse> {
    const { data } = await http.delete<RemoveEventMemberResponse>(
      path(eventId, `/members/${encodeURIComponent(userId)}`),
    )
    return data
  },
}
